import { useCallback, useRef } from 'react';
import apiClient from 'services/_infra/apiClient';

export const tourHasRenderableRoute = (tour) => {
  if (!tour) return false;
  if (tour?.encodedPolyline || tour?.polyline) return true;
  if (Array.isArray(tour?.legs) && tour.legs.length > 0) return true;
  if (Array.isArray(tour?.nodes) && tour.nodes.length > 1) return true;
  if (Array.isArray(tour?.stops) && tour.stops.length > 1) return true;
  return false;
};

export const resolveTourResponsePayload = (data, targetId) => {
  const key = `${targetId}`;
  const direct =
    data?.tour ||
    data?.trip ||
    data?.result ||
    data?.data ||
    (Array.isArray(data?.tours) ? data.tours.find((item) => `${item?.id}` === key) : null) ||
    (data && typeof data === 'object' ? data : null);
  return direct && `${direct?.id}` === key ? direct : null;
};

export const useScheduleBuilderTourHydration = ({
  selectedTourIdRef,
  focusTourOnMap,
  selectTour,
  setSelectedTourIds,
  setTours,
}) => {
  const pendingTourHydrationIdRef = useRef(null);

  const hydrateTourIfNeeded = useCallback(
    async (tour) => {
      if (!tour?.id || tourHasRenderableRoute(tour)) return;
      const key = `${tour.id}`;
      if (pendingTourHydrationIdRef.current === key) return;
      pendingTourHydrationIdRef.current = key;
      try {
        const res = await apiClient.get(`/responder/tours/${encodeURIComponent(key)}`);
        const resolved = resolveTourResponsePayload(res?.data, key);
        if (!resolved) return;
        setTours((prevTours) => {
          const current = Array.isArray(prevTours) ? prevTours : [];
          const index = current.findIndex((item) => `${item?.id}` === `${resolved?.id}`);
          if (index < 0) return [...current, resolved];
          const next = [...current];
          next[index] = { ...next[index], ...resolved };
          return next;
        });
        if (`${selectedTourIdRef.current ?? ''}` === `${resolved?.id ?? ''}`) {
          selectTour(resolved);
          focusTourOnMap(resolved);
          setSelectedTourIds(resolved?.id != null ? [resolved.id] : []);
        }
      } catch (error) {
        console.error('[ScheduleBuilder] Failed to hydrate selected tour', error);
      } finally {
        if (pendingTourHydrationIdRef.current === key) {
          pendingTourHydrationIdRef.current = null;
        }
      }
    },
    [focusTourOnMap, selectTour, selectedTourIdRef, setSelectedTourIds, setTours],
  );

  const resetPendingTourHydration = useCallback(() => {
    pendingTourHydrationIdRef.current = null;
  }, []);

  return {
    hydrateTourIfNeeded,
    resetPendingTourHydration,
  };
};
